import { useEffect, useState } from "react";
import { useLang } from "../context/LanguageContext";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { id: "solutions", key: "solutions" },
  { id: "framework", key: "framework" },
  { id: "blueprints", key: "blueprints" },
  { id: "ecosystem", key: "ecosystem" },
  { id: "services", key: "services" },
  { id: "insights", key: "insights" },
  { id: "contact", key: "contact" },
];

export const Navigation = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled ? "bg-white/90 backdrop-blur-md border-b border-[#0B1F3A]/10" : "bg-white/70 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <a href="#top" data-testid="nav-brand" className="flex items-center gap-2 group">
          <svg width="22" height="22" viewBox="0 0 22 22" className="text-[#0B1F3A]">
            <rect x="1" y="1" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <rect x="6" y="6" width="10" height="10" fill="currentColor" />
          </svg>
          <span className="font-display text-[15px] font-semibold tracking-tight text-[#0B1F3A]">
            MaxSolution<sup className="text-[8px] ml-0.5">™</sup>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              className="text-[13px] font-medium text-[#0B1F3A]/75 hover:text-[#0B1F3A] transition-colors"
            >
              {t.nav[l.key]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center border border-[#0B1F3A]/20 text-[11px] font-semibold tracking-wider">
            <button
              data-testid="lang-toggle-en"
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 transition-colors ${lang === "en" ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/70 hover:text-[#0B1F3A]"}`}
            >
              EN
            </button>
            <button
              data-testid="lang-toggle-id"
              onClick={() => setLang("id")}
              className={`px-2.5 py-1 transition-colors ${lang === "id" ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/70 hover:text-[#0B1F3A]"}`}
            >
              ID
            </button>
          </div>

          <a
            href="#contact"
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center px-4 py-2 bg-[#0B1F3A] text-white text-[12px] font-semibold tracking-wide hover:bg-[#0B1F3A]/90 transition-colors border border-[#0B1F3A]"
          >
            {t.nav.cta}
          </a>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-[#0B1F3A]"
            aria-label="Menu"
          >
            {open ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#0B1F3A]/10 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.id}`}
                className="py-3 border-b border-[#0B1F3A]/10 text-sm font-medium text-[#0B1F3A]"
              >
                {t.nav[l.key]}
              </a>
            ))}
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={() => setLang("en")}
                data-testid="mobile-lang-en"
                className={`px-3 py-1.5 text-xs font-semibold border border-[#0B1F3A]/20 ${lang === "en" ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]"}`}
              >EN</button>
              <button
                onClick={() => setLang("id")}
                data-testid="mobile-lang-id"
                className={`px-3 py-1.5 text-xs font-semibold border border-[#0B1F3A]/20 ${lang === "id" ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]"}`}
              >ID</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
